import { createSelector } from 'reselect';
import {
  filter,
  find,
  findIndex,
  findLastIndex,
  get,
  identity,
  isEmpty,
  isEqual,
  pick,
  reject,
  isString,
  isNull,
  flow,
  getOr,
} from 'lodash/fp';
import { TYPES, PATH_MODES } from 'constants/config';
import { capitalize } from 'utils/string_utils';
import { flattenByKey } from 'utils/fp';

const editingContentSelector = ({ editingContent }) => editingContent;
const contentsSelector = ({ contents }) => contents;
const relatedUrlsSelector = ({ relatedUrls }) => relatedUrls;
const libraryContentsSelector = ({ libraryContents }) => libraryContents;
const publishedAtSelector = ({ publishedAt }) => publishedAt;
const propsSelector = (state, props) => props;
const UIStateSelector = state => state.ui;
const modeSelector = state => state.mode;
const reportingSelector = state => state.reporting;
const loadingContentSelector = state => state.loadingContent;

const isNonWaitStepPredicate = props => props.type !== TYPES.WAITSTEP;

/**
 * All selectors expect `pathBuilder` as the state root
 */

function omitShallowRevisionKeys(contents) {
  return contents.map(
    pick([
      'id',
      'type',
      'position',
      'title',
      'dueIn',
      'description',
      'effect',
      'condition',
      'amount',
      'unit',
      'pathContentId',
      'meta',
      'certification',
    ]),
  );
}

export const getPathRevisionData = createSelector(identity, state => {
  const data = pick(['title', 'contents'], state);
  return {
    ...data,
    contents: omitShallowRevisionKeys(data.contents),
  };
});

export const isEditingContent = () =>
  createSelector(
    [editingContentSelector, propsSelector],
    (editingContent, { id, type }) => !!find({ id, type })(editingContent),
  );

export const isLoadingContent = () =>
  createSelector(
    [loadingContentSelector, propsSelector],
    (loadingContent, { id, type }) => loadingContent.includes(`${type}.${id}`),
  );

export const getContentViewState = () =>
  createSelector(
    [contentsSelector, editingContentSelector, propsSelector],
    (contents, editingContent, { id, type }) => {
      const contentBeingEdited = find({ id, type })(editingContent);
      if (!contentBeingEdited) return 'normal';
      return contentBeingEdited.view || 'editing';
    },
  );

export const isArchived = createSelector(
  identity,
  state => !!get('archivedAt.date')(state),
);

export const isPublishedSelector = createSelector(
  publishedAtSelector,
  publishedAt => !!get('date', publishedAt),
);

export const isJustAdded = () =>
  createSelector([identity, propsSelector], ({ contents }, { id, type }) =>
    get('justAdded', find({ id, type }, contents)),
  );

export const nonWaitStepContentSelector = createSelector(
  identity,
  ({ contents }) => reject({ type: TYPES.WAITSTEP })(contents),
);

export const placeholderContentSelector = createSelector(
  identity,
  ({ contents }) => filter({ type: TYPES.PLACEHOLDER })(contents),
);

export const isAddingCertification = createSelector(
  [identity, propsSelector],
  ({ contents }, { meta, certification }) => {
    return (
      certification ||
      get('certification.id', meta) ||
      find({ type: TYPES.CERTIFICATION })(contents)
    );
  },
);

const getTotalSteps = flow(
  flattenByKey('contents'),
  reject({ type: TYPES.WAITSTEP }),
  get('length'),
);

function getStepNumberOffset(contents, stepIndex) {
  const paths = filter({ type: TYPES.PATH }, contents.slice(0, stepIndex));
  return paths.reduce((offset, path) => {
    const flatListLength = getTotalSteps(path.contents) - 1;
    return offset + flatListLength;
  }, 0);
}

export const totalStepsSelector = createSelector(
  [nonWaitStepContentSelector],
  contents => getTotalSteps(contents),
);

export const getStepNumber = () =>
  createSelector(
    [nonWaitStepContentSelector, propsSelector],
    (contents, { id, type }) => {
      const index = findIndex({ id, type }, contents);
      return index + getStepNumberOffset(contents, index) + 1;
    },
  );

export const firstNonWaitStepIdxSelector = createSelector(
  identity,
  ({ contents }) => findIndex(isNonWaitStepPredicate)(contents),
);

export const lastNonWaitStepIdxSelector = createSelector(
  identity,
  ({ contents }) => findLastIndex(isNonWaitStepPredicate)(contents),
);

export const hasNoTrackLine = () =>
  createSelector(
    [
      identity,
      propsSelector,
      firstNonWaitStepIdxSelector,
      lastNonWaitStepIdxSelector,
    ],
    ({ contents }, { id, type }, firstNonWaitStepIdx, lastNonWaitStepIdx) => {
      const ownIndex = findIndex({ id, type })(contents);
      if (type === TYPES.WAITSTEP) {
        return ownIndex > lastNonWaitStepIdx || ownIndex < firstNonWaitStepIdx;
      }
      return ownIndex === lastNonWaitStepIdx;
    },
  );

export const canPublishPath = createSelector(
  [
    getPathRevisionData,
    placeholderContentSelector,
    nonWaitStepContentSelector,
    isArchived,
  ],
  ({ title }, placeholders, nonWaitStepContent, archived) => {
    const defaultResult = { value: true, reason: null };
    const rejectPublishing = reason => ({ value: false, reason });

    const WARNINGS = [
      'Paths that contain Placeholder Steps cannot be published',
      'Paths without any Steps cannot be published',
      'Paths without a name cannot be published',
      'Archived Paths cannot be published',
    ];

    if (!isEmpty(placeholders)) {
      return rejectPublishing(WARNINGS[0]);
    }

    if (isEmpty(nonWaitStepContent)) {
      return rejectPublishing(WARNINGS[1]);
    }

    if (isEmpty(title)) {
      return rejectPublishing(WARNINGS[2]);
    }

    if (archived) {
      return rejectPublishing(WARNINGS[3]);
    }

    return defaultResult;
  },
);

export const canEditPath = createSelector([isArchived], archived => {
  const defaultResult = { value: true, reason: null };
  const rejectEditing = reason => ({ value: false, reason });

  const MESSAGES = {
    ARCHIVED: 'Archived Paths cannot be edited',
  };

  if (archived) {
    return rejectEditing(MESSAGES.ARCHIVED);
  }

  return defaultResult;
});

export const hasUnsavedChanges = createSelector(
  [
    identity,
    getPathRevisionData,
    editingContentSelector,
    nonWaitStepContentSelector,
  ],
  (
    { lastRevision, publishedAt, certification },
    revisionData,
    editingContent,
    nonWaitStepContent,
  ) => {
    const isPublished = !!get('date', publishedAt);
    const defaultResult = { value: true, reason: null };
    const rejectSaving = reason => ({ value: false, reason });

    const WARNINGS = [
      `Give this Path a name before ${isPublished ? 'updating' : 'saving'}`,
      `There are no ${isPublished ? 'updates to make' : 'changes to save'}`,
      `Finish editing all steps before ${isPublished ? 'updating' : 'saving'}`,
      'Published Paths must have at least one step',
      `Give this Certification Path a name before ${
        isPublished ? 'updating' : 'saving'
      }`,
      'Published Certification Paths must have at least one step',
    ];

    if (isPublished && isEmpty(nonWaitStepContent)) {
      return certification
        ? rejectSaving(WARNINGS[5])
        : rejectSaving(WARNINGS[3]);
    }

    if (isEmpty(revisionData.title)) {
      return certification
        ? rejectSaving(WARNINGS[4])
        : rejectSaving(WARNINGS[0]);
    }

    if (!isEmpty(editingContent)) {
      return rejectSaving(WARNINGS[2]);
    }

    if (isEqual(lastRevision, revisionData)) {
      return rejectSaving(WARNINGS[1]);
    }

    return defaultResult;
  },
);

export const validateWaitStep = () =>
  createSelector([identity, propsSelector], ({ contents, mode }, { index }) => {
    const defaultValidation = { invalid: false };
    const WARNING_MESSAGES = [
      'Wait Steps at the beginning of a Path are ignored.',
      'Wait Steps at the end of a Path are ignored.',
      'Wait Steps immediately following another Wait Step are ignored.',
    ];

    const generateInvalidation = warningCode => ({
      invalid: true,
      message: WARNING_MESSAGES[warningCode],
    });

    const hasNoWaitStepAfter = () =>
      isEmpty(contents.slice(index + 1).filter(isNonWaitStepPredicate));

    if (index === 0) {
      return generateInvalidation(0);
    }

    if (mode !== PATH_MODES.EDIT && hasNoWaitStepAfter()) {
      return generateInvalidation(1);
    }

    const prevStep = contents[index - 1];
    if (prevStep && prevStep.type.match(/waitstep/i)) {
      return generateInvalidation(2);
    }

    return defaultValidation;
  });

export const isEditingUIElementByName = createSelector(
  [UIStateSelector, propsSelector],
  (ui, name) => get(`editing${capitalize(name)}`, ui),
);

export const chromeExtensionMappings = createSelector(
  relatedUrlsSelector,
  relatedUrls =>
    relatedUrls.filter(
      ({ relatedUrl }) => isNull(relatedUrl.name) && isString(relatedUrl.url),
    ),
);

export const webWidgetMappings = createSelector(
  relatedUrlsSelector,
  relatedUrls =>
    relatedUrls.filter(
      ({ relatedUrl }) => isNull(relatedUrl.url) && isString(relatedUrl.name),
    ),
);

export const isAddedToLibraryForEveryone = createSelector(
  [libraryContentsSelector, propsSelector],
  (libraryContents, everyoneGroup) =>
    libraryContents.some(content => content.group.id === everyoneGroup?.id),
);

export const hasRelatedUrls = createSelector(
  relatedUrlsSelector,
  relatedUrls => !isEmpty(relatedUrls),
);

const createModeSelector = mode =>
  createSelector(modeSelector, currentMode => currentMode === mode);

export const isEditMode = createModeSelector(PATH_MODES.EDIT);
export const isViewMode = createModeSelector(PATH_MODES.VIEW);
export const isReportingMode = createModeSelector(PATH_MODES.REPORTING);

export const modeSelectors = {
  isEdit: isEditMode,
  isView: isViewMode,
  isReporting: isReportingMode,
};

export const shouldRenderTabs = createSelector(
  [isPublishedSelector, modeSelector],
  (isPublished, mode) =>
    [isPublished, [PATH_MODES.VIEW, PATH_MODES.REPORTING].includes(mode)].every(
      Boolean,
    ),
);

export const hasReportingPermission = createSelector(
  [reportingSelector, propsSelector],
  (reporting, name) => getOr(false, `permissions.${name}`, reporting),
);
